import React, { useState } from "react";

export function repairCommand(row) {
  if (row.reason !== "Load ID change not allowed") return null;
  const po = String(row.poNumber || "");
  const load = String(row.incomingLoad || "");
  if (![po, load].every(value => /^[0-9]{1,32}$/.test(value) && /[1-9]/.test(value))) return null;
  return `python tools\\repair_routing_load_change.py --po ${po} --target-load ${load}`;
}

export default function RoutingImportWarning({ summary, onDismiss }) {
  const [copyStatus, setCopyStatus] = useState("");
  if (!summary?.skipped) return null;
  const rows = summary.skippedRows || [];
  const recovery = rows.some(row => row.reason === "Load ID change not allowed");
  async function copy(command) {
    try {
      await navigator.clipboard.writeText(command);
      setCopyStatus("Repair command copied.");
    } catch {
      setCopyStatus("Could not copy. Select and copy the command text below.");
    }
  }
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{recovery
            ? "Load ID change blocked — Admin recovery required"
            : "Some Routing updates were skipped. Review the reason for each row below."}</p>
          <p className="mt-1 text-xs">Imported: {summary.imported ?? 0} · Skipped: {summary.skipped}</p>
        </div>
        <button type="button" onClick={onDismiss} className="rounded border border-current px-2 py-1 text-xs font-semibold">Dismiss</button>
      </div>
      {recovery && <div className="mt-3">
        <p>PortPilot protected the existing Load because Walmart is requesting a different Load ID.</p>
        <p>Do not repeatedly re-import the Routing file.</p>
        <p className="mt-2 font-semibold">Recovery workflow</p>
        <ol className="ml-5 list-decimal space-y-1">
          <li>In iLS, resume the outbound to New.</li>
          <li>Confirm the outbound is unpacked: SQty = 0 and no shipment has been sent.</li>
          <li>Run the Load ID Correction repair package using the PO and Incoming Load shown below.</li>
          <li>Run the dry-run first. Execute only when it returns: SAFE_TO_REPAIR = YES</li>
          <li>After the repair succeeds, re-pack the outbound in iLS.</li>
          <li>Return to Routing Status and click Update WMS Status.</li>
          <li>Confirm the new Load ID and Packed quantities.</li>
          <li>If the repair tool reports BOL_REGEN_REQUIRED = YES, generate a NEW BOL and
            confirm the BOL shows the new Load ID. Do not use the old-Load BOL.</li>
          <li>Generate 856 only after the shipment is actually shipped.</li>
        </ol>
        <p className="mt-2">Repair package: <code>tools\repair_routing_load_change.py</code></p>
      </div>}
      <p role="status" className="mt-1 text-xs">{copyStatus}</p>
      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead><tr><th className="pr-4">PO</th><th className="pr-4">Existing Load</th><th className="pr-4">Incoming Load</th><th className="pr-4">WMS Status</th><th>Reason</th>{recovery && <th>Admin recovery</th>}</tr></thead>
          <tbody>{rows.map((row, index) => {
            const command = repairCommand(row);
            return <tr key={`${row.poNumber}-${row.incomingLoad}-${index}`} className="border-t border-amber-200">
              <td className="py-1 pr-4">{row.poNumber}</td><td className="pr-4">{row.existingLoad || "Blank"}</td>
              <td className="pr-4">{row.incomingLoad || "Blank"}</td><td className="pr-4">{row.wmsStatus || "Unknown"}</td><td>{row.reason}</td>
              {recovery && <td className="p-2">{command && <>
                <code className="block select-text whitespace-nowrap">{command}</code>
                <button type="button" className="mt-1 rounded border border-current px-2 py-1 font-semibold"
                  onClick={() => copy(command)}>Copy Repair Command</button>
              </>}</td>}
            </tr>;
          })}</tbody>
        </table>
      </div>
    </div>
  );
}
